import { api } from "@/services/api"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import type {
  WeeklyObjective,
  WeeklyOfWeekly,
} from "../entities/weekly-objectives/weekly-objectives"
import { toast } from "sonner"
import { useWeeklyObjectivesStore } from "../stores/use-weekly-objectives-store"
import useInvalidateStudyPlannerQueries from "@/modules/activities/hooks/use-invalidate-queries"

interface UpdateWeeklyObjectiveData {
  id: string
  date: string
}

export default function useUpdateWeeklyObjective() {
  const queryClient = useQueryClient()
  const setWeeklyObjectives = useWeeklyObjectivesStore((state) => state.setWeeklyObjectives)
  const handleRefetchQueries = useInvalidateStudyPlannerQueries()

  const mutation = useMutation({
    mutationFn: ({ id, ...data }: UpdateWeeklyObjectiveData) => {
      return api.cronos.weeklyObjectives.update(id, data)
    },
    onMutate: async ({ id, date }) => {
      await queryClient.cancelQueries({ queryKey: ["planner-get-weekly-objectives"] })
      const previousData = queryClient.getQueryData<{ weeks: WeeklyOfWeekly[] }>([
        "planner-get-weekly-objectives",
      ])
      if (!previousData) return { previousData: null }
      // Find the objective and move it to the new date
      let movedObjective: WeeklyObjective | null = null
      let destinationColumnIndex = -1
      const newWeeks = previousData.weeks.map((week) => {
        const objIdx = week.objectives.findIndex((o) => o.id === id)
        if (objIdx !== -1) {
          movedObjective = { ...week.objectives[objIdx], weekDateTime: date }
        }
        return { ...week, objectives: week.objectives.filter((o) => o.id !== id) }
      })
      // Find the destination column by date
      destinationColumnIndex = newWeeks.findIndex((week) => {
        const start = new Date(week.startDate)
        const end = new Date(week.endDate)
        const d = new Date(date)
        return d >= start && d <= end
      })
      if (movedObjective && destinationColumnIndex !== -1) {
        newWeeks[destinationColumnIndex] = {
          ...newWeeks[destinationColumnIndex],
          objectives: [...newWeeks[destinationColumnIndex].objectives, movedObjective],
        }
      }
      queryClient.setQueryData(["planner-get-weekly-objectives"], {
        ...previousData,
        weeks: newWeeks,
      })
      setWeeklyObjectives(newWeeks)
      return { previousData }
    },
    onError: (_err, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(["planner-get-weekly-objectives"], context.previousData)
        setWeeklyObjectives(context.previousData.weeks)
      }
      toast.error("Error al mover el objetivo semanal")
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ["planner-get-weekly-objectives"],
      })
      queryClient.invalidateQueries({
        queryKey: ["study-get-activities"],
      })
    },
    onSuccess: () => {
      handleRefetchQueries()
    },
  })

  return mutation
}
